import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageIntro } from "@/components/SellerToolsNav";

const MARKETS = [
  "Poshmark",
  "Mercari",
  "Depop",
  "Grailed",
  "Facebook Marketplace",
  "Meesho",
  "Myntra",
  "AJIO",
  "Nykaa",
  "Tata CLiQ",
  "OLX",
];

export default function Extension() {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const apiBase = (typeof document !== "undefined" && document.documentElement.dataset.shoptosaApi) || "";
      const res = await api.get("/seller/extension/download", {
        responseType: "blob",
        params: { origin, api: apiBase },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shoptosa-helper.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded shoptosa-helper.zip");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[800px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="seller-extension">
      <PageIntro
        kicker="Marketplaces"
        title="Listing helper"
        blurb="Download a Chrome/Edge helper for marketplaces that have no public listing API. Stay signed in on their site. Sync reads your closet from that tab. To list, the helper copies or fills fields — you click List yourself."
        actions={
          <Link to="/seller/connections" className="px-4 py-2.5 border border-aura-border text-sm rounded-lg">
            Back to Connections
          </Link>
        }
      />

      <div className="bg-aura-surface border border-aura-border rounded-xl p-5 mb-6">
        <div className="text-sm font-medium mb-2">Works on</div>
        <div className="flex flex-wrap gap-2">
          {MARKETS.map((name) => (
            <span key={name} className="text-xs px-2 py-1 rounded-full border border-aura-border text-aura-muted">
              {name}
            </span>
          ))}
        </div>
      </div>

      <ol className="space-y-4 mb-8">
        <li className="bg-aura-surface border border-aura-border rounded-xl p-5">
          <div className="font-medium mb-1">1. Download the helper</div>
          <p className="text-sm text-aura-muted mb-3">Unzip the file. You should see manifest.json in that folder.</p>
          <button
            type="button"
            disabled={busy}
            onClick={download}
            data-testid="download-helper"
            className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-lg"
          >
            {busy ? "Downloading…" : "Download shoptosa-helper.zip"}
          </button>
        </li>
        <li className="bg-aura-surface border border-aura-border rounded-xl p-5">
          <div className="font-medium mb-1">2. Load unpacked in Chrome or Edge</div>
          <p className="text-sm text-aura-muted">
            Open <span className="font-mono">chrome://extensions</span> (or <span className="font-mono">edge://extensions</span>),
            turn on Developer mode, click Load unpacked, and select the unzipped folder.
          </p>
        </li>
        <li className="bg-aura-surface border border-aura-border rounded-xl p-5">
          <div className="font-medium mb-1">3. Sign in on Shoptosa, then the marketplace</div>
          <p className="text-sm text-aura-muted">
            Open this site while signed in as a seller so the helper can load your listings. Then open Poshmark, Mercari,
            Grailed, Facebook Marketplace, Meesho, Myntra, AJIO, Nykaa, Tata CLiQ, or OLX in your own tab.
          </p>
        </li>
        <li className="bg-aura-surface border border-aura-border rounded-xl p-5">
          <div className="font-medium mb-1">4. Fill the sell form, then click List — Sync reads the closet</div>
          <p className="text-sm text-aura-muted">
            On Connections, List on {MARKETS[0]} opens the Shoptosa listing form. Click List on {MARKETS[0]} there to type title, description, and price on their create form. You click List on their site — Shoptosa cannot publish for you. After it is live, Sync reads listed/sold tiles from the closet tab you have open.
          </p>
        </li>
      </ol>

      <p className="text-xs text-aura-muted">
        eBay, Etsy, Amazon, Shopify, and Flipkart connect from{" "}
        <Link to="/seller/connections" className="text-aura-cyan">Connections</Link>
        {" "}through official APIs. Depop, Poshmark, and Mercari use this helper — Depop has no public listing API for third-party apps.
      </p>
    </div>
  );
}
